import { useMemo, useCallback, useRef, useEffect, useState, type Ref } from "react"
import OpLayoutEngineRenderer from "connected-components/op-layout-engine/renderer"
import { TEMPLATES } from "connected-components/op-layout-engine/templates"
import type { OpLayoutEngineRendererActions } from "connected-components/op-layout-engine/types"
import type { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { useRemoveModule } from "../hooks/use-remove-module"
import { useAddModule } from "../hooks/use-add-module"
import { useUpdateModule } from "../hooks/use-update-module"
import { panelsSlice, isDefaultModulesSnapshot, EMPTY_MODULES } from "../store/panels-slice"
import { useHasThematicBetasContentSet } from "../hooks/use-has-thematic-betas-content-set"
import { DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID, DEFAULT_THEMATIC_SORT_METRIC_ID } from "../modules/constants"
import {
  DashboardOpLayoutModuleDependenciesContext,
  type DashboardOpLayoutModuleDependencies,
} from "../context/dashboard-op-layout-module-dependencies"
import { useDashboardOpLayoutEnginePersistence } from "../hooks/use-op-layout-engine-persistence"
import { useInstanceWorkspaceContext } from "../context/instance-workspace-context"
import { useDashboardModuleActions } from "../hooks/use-dashboard-module-actions"
import { createModuleComponents } from "../helpers/module-renderers"
import {
  registerDashboardLayoutPanelState,
  unregisterDashboardLayoutPanelState,
} from "../helpers/dashboard-layout-panel-state"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import type { SerializedDockview } from "dockview"
import { ERROR } from "helpers/data-statuses"
import { warn } from "helpers/dev-logger"
import { initFromLayoutConfig } from "../layouts/init-from-layout-config"
import { useBackingLayoutConfig } from "../hooks/use-backing-layout-config"
import { MODULE_TYPES } from "../modules/types"

interface DashboardModulesRendererProps {
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  hasMultipleInstruments?: boolean
  contentSets?: unknown[]
  modelMetadata?: unknown
  dockviewLayoutRef?: Ref<OpLayoutEngineRendererActions | null>
  onLayoutReady?: (actions: OpLayoutEngineRendererActions | null) => void
}

/**
 * Renders dashboard modules using OpLayoutEngine
 */
export default function DashboardModulesRenderer({
  instrumentsWithMetadata,
  hasMultipleInstruments,
  contentSets,
  modelMetadata,
  dockviewLayoutRef,
  onLayoutReady,
}: DashboardModulesRendererProps) {
  const dispatch = useAppDispatch()
  const dashboardId = useDashboardId()
  const panels = useAppSelector((s) => s.dashboard.panels[dashboardId])
  const modules = panels?.modules ?? EMPTY_MODULES
  const isModulesPreferencesLoaded = panels?.isPreferencesLoaded ?? false
  const removeModule = useRemoveModule()
  const updateModule = useUpdateModule()

  const { hasThematicBetasContentSet } = useHasThematicBetasContentSet()

  const addModule = useAddModule()

  // Auto-inject the thematic-betas panel when an entitled account's layout is
  // exactly the unentitled five-default snapshot — covers users who gained
  // entitlement after their dashboard was first seeded. The `.some()` + snapshot
  // checks block re-injection while the panel is present; a manual removal by
  // the user then matches the snapshot again, so the effect would re-add it.
  // That's the intended pre-RTK behaviour; we preserve it rather than latch.
  useEffect(() => {
    if (!isModulesPreferencesLoaded || !hasThematicBetasContentSet) return
    if (modules.some((m) => m.config?.initialView === DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID)) return
    if (!isDefaultModulesSnapshot(modules)) return
    addModule({
      type: MODULE_TYPES.FACTORS,
      config: {
        initialView: DEFAULT_FACTORS_THEMATIC_CONTENT_SET_ID,
        sortMetricId: DEFAULT_THEMATIC_SORT_METRIC_ID,
        title: "Thematic Betas",
      },
    })
  }, [isModulesPreferencesLoaded, hasThematicBetasContentSet, modules, addModule])

  // Instance mode: the engine loads the seeded arrangement (workspace/layout), not the
  // dockviewLayout preference. Null outside instance mode (provider absent → today's path).
  const instanceWorkspace = useInstanceWorkspaceContext()
  const opLayoutEnginePersistence = useDashboardOpLayoutEnginePersistence(
    modules,
    isModulesPreferencesLoaded,
    instanceWorkspace?.instanceArrangement ?? null,
  )
  const { handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule } = useDashboardModuleActions(modules, {
    removeModule,
    updateModule,
    addModule,
  })

  const layoutEngineActionsRef = useRef<OpLayoutEngineRendererActions | null>(null)

  useEffect(() => {
    return dashboardEvents.on<{ panelId: string }>(
      DASHBOARD_EVENTS.DRILLDOWN_FOCUS_PANEL,
      ({ panelId }) => layoutEngineActionsRef.current?.focusPanel(panelId),
    )
  }, [])

  useEffect(() => {
    registerDashboardLayoutPanelState({
      isPanelMounted: (panelId) => layoutEngineActionsRef.current?.isPanelMounted(panelId) ?? false,
      ensurePanel: (panelId) => layoutEngineActionsRef.current?.ensurePanel(panelId) ?? false,
    })
    return unregisterDashboardLayoutPanelState
  }, [])

  // Reset restores this dashboard to its backing layout (not the global default): re-seed slices, then
  // reload the layout arrangement. Trigger comes over dashboardEvents — the Layout menu is outside
  // InstanceWorkspaceProvider, and shares this resolution via useBackingLayoutConfig to gate its own
  // "Reset layout" menu item on the same loaded state. [APP-3309]
  const { resetInstanceId, backingLayoutConfig, layoutStatus } = useBackingLayoutConfig(dashboardId)

  // Nonce drives the apply effect unconditionally: the re-seed dispatch is a no-op when only the
  // arrangement diverged.
  const [reseedNonce, setReseedNonce] = useState(0)
  const pendingReseedArrangementRef = useRef<SerializedDockview | null>(null)
  const isReseedPendingRef = useRef(false)

  const pendingResetApiRef = useRef<Parameters<OpLayoutEngineRendererActions["resetLayout"]>[0] | null>(null)
  const didRequestResetRef = useRef(false)
  const [layoutActionsVersion, setLayoutActionsVersion] = useState(0)

  // Runs once the re-seed has committed the layout's modules, then reloads the saved arrangement.
  // Also keyed on layoutActionsVersion so it retries once getApi() becomes ready. [APP-3309]
  useEffect(() => {
    if (!isReseedPendingRef.current) return
    const api = layoutEngineActionsRef.current?.getApi?.()
    if (!api) return
    isReseedPendingRef.current = false
    const arrangement = pendingReseedArrangementRef.current
    pendingReseedArrangementRef.current = null
    if (arrangement) {
      try {
        api.fromJSON(arrangement)
      } catch (error) {
        warn("Dockview arrangement failed to apply; falling back to a full layout reset", error)
        layoutEngineActionsRef.current?.resetLayout(api)
      }
    } else {
      layoutEngineActionsRef.current?.resetLayout(api)
    }
  }, [reseedNonce, layoutActionsVersion])

  useEffect(() => {
    if (!didRequestResetRef.current || !pendingResetApiRef.current) {
      return
    }
    if (!isDefaultModulesSnapshot(modules, hasThematicBetasContentSet)) {
      return
    }

    const runEngineReset = layoutEngineActionsRef.current?.resetLayout
    if (!runEngineReset) {
      return
    }

    const api = pendingResetApiRef.current
    runEngineReset(api)
    pendingResetApiRef.current = null
    didRequestResetRef.current = false
  }, [modules, hasThematicBetasContentSet, layoutActionsVersion])

  // "Reset layout" trigger from the header's Layout menu (cross-boundary via dashboardEvents — the menu
  // lives outside InstanceWorkspaceProvider). A real instance restores its backing layout; a legacy
  // slug/"default" dashboard (no backing layout, e.g. pre lazy-migration) resets to the default modules
  // — the same path as the flag-off toolbar Reset — so Reset is never a silent no-op. Declared after the
  // legacy reset state above so both branches' refs are in scope.
  useEffect(() => {
    return dashboardEvents.on<{ dashboardId: string }>(
      DASHBOARD_EVENTS.RESET_LAYOUT_REQUESTED,
      ({ dashboardId: requestedId }) => {
        if (requestedId !== dashboardId) return
        const api = layoutEngineActionsRef.current?.getApi?.()
        if (!api) return
        if (resetInstanceId) {
          // If the layout hasn't loaded, do nothing (LayoutActionsMenu already disables the trigger);
          // warn on ERROR so it's not silently indistinguishable from "still loading". [APP-3309]
          if (!backingLayoutConfig) {
            if (layoutStatus === ERROR) {
              warn("Reset requested but the backing layout failed to load", { dashboardId })
            }
            return
          }
          initFromLayoutConfig(dispatch, dashboardId, backingLayoutConfig)
          // Verbatim Dockview blob the server validated on write (ADR 0003) — loaded as-is, not reshaped.
          pendingReseedArrangementRef.current = backingLayoutConfig.arrangement
            ? (backingLayoutConfig.arrangement as unknown as SerializedDockview)
            : null
          isReseedPendingRef.current = true
          setReseedNonce((nonce) => nonce + 1)
        } else {
          // Legacy / non-instance dashboard: no backing layout to restore, so reset to default modules.
          dispatch(panelsSlice.actions.resetModulesToDefault({ dashboardId, hasThematicBetasContentSet }))
          pendingResetApiRef.current = api
          didRequestResetRef.current = true
          setLayoutActionsVersion((version) => version + 1)
        }
      },
    )
  }, [dashboardId, resetInstanceId, backingLayoutConfig, layoutStatus, hasThematicBetasContentSet, dispatch])

  const wrappedOnLayoutReady = useCallback(
    (actions: OpLayoutEngineRendererActions | null) => {
      layoutEngineActionsRef.current = actions
      // Expose a live arrangement reader to the workspace writer (instance mode); cleared when the
      // engine unmounts so the writer never reads a stale api.
      if (instanceWorkspace) {
        instanceWorkspace.arrangementGetterRef.current = actions
          ? () => (actions.getApi()?.toJSON() ?? null) as Record<string, unknown> | null
          : null
      }
      // Also retrigger a pending reseed that missed its window while the engine wasn't mounted. [APP-3309]
      if (actions && (didRequestResetRef.current || isReseedPendingRef.current)) {
        setLayoutActionsVersion((version: number) => version + 1)
      }

      if (!onLayoutReady) {
        return
      }
      if (!actions) {
        onLayoutReady(null)
        return
      }
      onLayoutReady({
        ...actions,
        resetLayout: (api) => {
          didRequestResetRef.current = true
          pendingResetApiRef.current = api
          dispatch(panelsSlice.actions.resetModulesToDefault({ dashboardId, hasThematicBetasContentSet }))
        },
      })
    },
    [onLayoutReady, dispatch, dashboardId, hasThematicBetasContentSet, instanceWorkspace],
  )

  const components = useMemo(() => {
    return createModuleComponents(modules, {
      instrumentsWithMetadata,
      hasMultipleInstruments,
      contentSets,
      modelMetadata,
      updateModule,
      onRemoveModule: handleRemoveModule,
      onUpdateModuleTitle: handleUpdateModuleTitle,
      onDuplicateModule: handleDuplicateModule,
    })
  }, [modules, contentSets, hasMultipleInstruments, instrumentsWithMetadata, modelMetadata, updateModule, handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule])

  const dashboardOpLayoutModuleDependencies = useMemo((): DashboardOpLayoutModuleDependencies => ({
    instrumentsWithMetadata,
    hasMultipleInstruments,
    contentSets,
    modelMetadata,
  }), [instrumentsWithMetadata, hasMultipleInstruments, contentSets, modelMetadata])

  return (
    <DashboardOpLayoutModuleDependenciesContext.Provider value={dashboardOpLayoutModuleDependencies}>
      <OpLayoutEngineRenderer
        layoutRef={dockviewLayoutRef ?? null}
        onLayoutReady={wrappedOnLayoutReady}
        components={components}
        template={TEMPLATES.HIERARCHICAL}
        persistence={opLayoutEnginePersistence}
        className="dashboard-op-layout"
      />
    </DashboardOpLayoutModuleDependenciesContext.Provider>
  )
}
